/* eslint-disable no-unused-vars */
import { addTransportItemsCommand, addTransportItemsCondition, setTransportItemsActivity } from 'app/actions/map';
import { addEventQueue, addParkingAlert } from 'app/actions/notification';
import { setWebsocketConnect, setWebsocketStatus } from 'app/actions/websocket';
import { transportConditionLoad, transportLoad } from 'app/store/map';
import { createNonemptyOilParkingAlertTracker } from 'app/utils/nonemptyOilParkingAlert';
import { batch } from 'react-redux';
// ПО промежуточного слоя использует действия для взаимодействия с соединением WebSocket,
// включая подключение, отключение, отправку и получение сообщений.
export const websocketMiddleware = function () {
    let websocket = null, hasOpened = false, connectedPayload = null, reconnectionInterval = null, messageQueue = [], messageInterval = null, visibilityHandler = null, hiddenQueue = [], // Очередь для критичных сообщений при неактивной вкладке
    isProcessingHiddenQueue = false, // Флаг обработки накопленной очереди
    hiddenSince = null, // Время когда вкладка стала неактивной
    hasDisconnected = false, isListReloading = false, isListReloadPending = false, lastListReloadAt = 0, listReloadTimeout = null;
    // Максимальный размер очереди сообщений для предотвращения переполнения памяти.
    const MAX_QUEUE_SIZE = 500;
    // Максимальный размер очереди для неактивной вкладки (только критичные сообщения).
    // Рассчитано: при частоте 1 событие/сек за 10 минут = 600 событий, берем 500 с запасом.
    const MAX_HIDDEN_QUEUE_SIZE = 500;
    // Размер порции для обработки накопленных сообщений при возврате на вкладку.
    // Увеличено для более эффективной обработки (современные браузеры справляются).
    const BATCH_SIZE = 50;
    // Порог для перезапроса: если очередь больше этого значения, делаем перезапрос из БД.
    // Увеличено, чтобы чаще использовать порционную обработку вместо перезапроса.
    const RELOAD_THRESHOLD = 200;
    // Порог времени неактивности в миллисекундах (10 минут) для перезапроса.
    // Увеличено для более разумного порога перезапроса.
    const INACTIVE_TIME_THRESHOLD = 10 * 60 * 1000;
    // Список event_id, которые требуют создания уведомлений
    const NOTIFICATION_EVENT_IDS = [1, 12];
    // Сохранить ссылку на store для доступа к состоянию.
    let store = null;
    const parkingAlertTracker = createNonemptyOilParkingAlertTracker({
        dutDefault: config.conditions.dut.default,
    });
    const maybeNotifyNonemptyOilParking = (dispatch, data) => {
        var _a, _b;
        if (!store || !data || data.transport_id == null) {
            return;
        }
        const transportItems = ((_a = store.getState().map) === null || _a === void 0 ? void 0 : _a.transportItems) || [];
        const transport = transportItems.find((item) => String(item.id) === String(data.transport_id));
        if (!transport) {
            return;
        }
        const conditions = transport.conditions;
        const previousData = Array.isArray(conditions) && conditions.length !== 0 ? (_b = conditions[conditions.length - 1]) === null || _b === void 0 ? void 0 : _b.data : null;
        if ((previousData === null || previousData === void 0 ? void 0 : previousData.datetime) && data.datetime && moment.utc(data.datetime).isBefore(moment.utc(previousData.datetime))) {
            return;
        }
        if (!parkingAlertTracker.evaluate(transport, data, previousData)) {
            return;
        }
        dispatch(addParkingAlert({
            transport_id: data.transport_id,
            datetime: data.datetime,
            geofence_id: data.geofence_id,
            geofence_name: data.geofence_name,
        }));
    };
    // Перезапросить снимок ТС с сервера (GET /map/transport), если пропущены события WebSocket.
    // Общий debounce для возврата на вкладку и переподключения; при скрытой вкладке откладывается до возврата.
    const requestTransportListReload = () => {
        var _a, _b, _c, _d, _e, _f;
        if (!store) {
            return;
        }
        if (document.visibilityState === 'hidden') {
            isListReloadPending = true;
            return;
        }
        const state = store.getState();
        if (!((_a = state.session) === null || _a === void 0 ? void 0 : _a.session) || !location.pathname.startsWith('/map') || ((_d = (_c = (_b = state.map) === null || _b === void 0 ? void 0 : _b.transportItems) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) === 0) {
            isListReloadPending = false;
            return;
        }
        const { reloadDebounceMs } = config.mapTransportListOptions;
        const elapsed = Date.now() - lastListReloadAt;
        if (isListReloading || elapsed < reloadDebounceMs) {
            if (!listReloadTimeout) {
                listReloadTimeout = setTimeout(() => {
                    listReloadTimeout = null;
                    requestTransportListReload();
                }, Math.max(1000, reloadDebounceMs - elapsed));
            }
            return;
        }
        isListReloadPending = false;
        isListReloading = true;
        lastListReloadAt = Date.now();
        store
            .dispatch(transportLoad())
            .catch((error) => console.error(error))
            .then(() => {
            isListReloading = false;
        });
        const selectedTransportId = (_f = (_e = state.map) === null || _e === void 0 ? void 0 : _e.transportSelectedItem) === null || _f === void 0 ? void 0 : _f.id;
        if (selectedTransportId) {
            store.dispatch(transportConditionLoad(selectedTransportId));
        }
    };
    const cancelTransportListReload = () => {
        if (listReloadTimeout) {
            clearTimeout(listReloadTimeout);
            listReloadTimeout = null;
        }
        isListReloadPending = false;
        hasDisconnected = false;
    };
    // Обработчик событий подключения через WebSocket.
    const connectEvent = (dispatch) => {
        // Проверить, не открыто ли уже соединение.
        if (websocket && (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING)) {
            return; // Соединение уже открыто или открывается, не создаем новое.
        }
        // Закрыть старое соединение, если оно есть в другом состоянии.
        if (websocket) {
            closeOpenConnection(1000, 'Reconnecting');
        }
        // Очистить старый интервал обработки сообщений, если он есть.
        if (messageInterval) {
            clearInterval(messageInterval);
            messageInterval = null;
            messageQueue = [];
            hiddenQueue = [];
            isProcessingHiddenQueue = false;
        }
        // Удалить старый обработчик видимости вкладки, если он есть.
        if (visibilityHandler) {
            document.removeEventListener('visibilitychange', visibilityHandler);
            visibilityHandler = null;
        }
        // Сохранить последнюю полезную нагрузку подключения,
        // чтобы, при автоматическом переподключении повторно использовать ее.
        // eslint-disable-next-line no-useless-escape
        connectedPayload = location.origin.replace(/(http)(s)?\:\/\//, 'ws$2://') + '/ws';
        // Установить соединение с сервером по технологии WebSocket.
        try {
            websocket = new WebSocket(connectedPayload);
        }
        catch (error) {
            console.error('WebSocket connection error:', error);
            return;
        }
        websocket.addEventListener('error', (event) => {
            console.error('WebSocket error:', event);
            closeOpenConnection(1000, 'Reconnecting');
        });
        // Обработка открытия соединения
        websocket.addEventListener('open', (event) => {
            // Очистить все невыполненные попытки повторного подключения.
            if (reconnectionInterval) {
                clearInterval(reconnectionInterval);
                reconnectionInterval = null;
            }
            // Изменить статус WebSocket подключения.
            dispatch(setWebsocketConnect(event));
            // Отследить, что мы смогли открыть соединение. Мы можем использовать этот флаг
            // для обработки ошибок позже, гарантируя, что мы не попытаемся переподключиться, когда
            // соединение вообще никогда не открывалось.
            hasOpened = true;
            // После обрыва события за время без связи потеряны - подтянуть актуальный снимок ТС.
            if (hasDisconnected) {
                hasDisconnected = false;
                requestTransportListReload();
            }
        });
        // Обработка закрытия соединения.
        websocket.addEventListener('close', (event) => {
            // Очистить все невыполненные попытки такта цикла событий.
            if (messageInterval) {
                clearInterval(messageInterval);
                messageInterval = null;
            }
            // Очистить очереди сообщений при закрытии соединения.
            messageQueue = [];
            hiddenQueue = [];
            isProcessingHiddenQueue = false;
            // Условно пытаться переподключиться, если включено и применимо.
            if (canAttemptReconnect()) {
                hasDisconnected = true;
                restoreBrokenConnection(dispatch);
            }
        });
        // Обработка события сообщения.
        websocket.addEventListener('message', (event) => {
            try {
                // Получить тело сообщения из закодированной строки.
                const { type, data } = JSON.parse(event.data);
                // Если вкладка неактивна, сохранять только критичные сообщения.
                if (document.visibilityState === 'hidden') {
                    // Сохранять только критичные типы сообщений (conditions и activity).
                    if (type === 'blackbox' || type === 'wialon_data' || type === 'activity') {
                        // Ограничить размер очереди неактивной вкладки.
                        if (hiddenQueue.length >= MAX_HIDDEN_QUEUE_SIZE) {
                            hiddenQueue.shift(); // Удалить самое старое сообщение.
                        }
                        // Сохранить критичное сообщение в очередь неактивной вкладки.
                        if (type === 'blackbox' || type === 'wialon_data') {
                            hiddenQueue.push(addTransportItemsCondition(data));
                            if (NOTIFICATION_EVENT_IDS.includes(data === null || data === void 0 ? void 0 : data.event_id)) {
                                dispatch(addEventQueue(data));
                            }
                            maybeNotifyNonemptyOilParking(dispatch, data);
                        }
                        else if (type === 'activity') {
                            hiddenQueue.push(setTransportItemsActivity(data));
                        }
                    }
                    // Пропустить некритичные сообщения при неактивной вкладке.
                    return;
                }
                // Обработка сообщений при активной вкладке.
                switch (type) {
                    case 'blackbox':
                    case 'wialon_data':
                        // Ограничить размер очереди, удаляя самые старые сообщения при переполнении.
                        if (messageQueue.length >= MAX_QUEUE_SIZE) {
                            messageQueue.shift(); // Удалить самое старое сообщение.
                        }
                        messageQueue.push(addTransportItemsCondition(data));
                        if (NOTIFICATION_EVENT_IDS.includes(data === null || data === void 0 ? void 0 : data.event_id)) {
                            dispatch(addEventQueue(data));
                        }
                        maybeNotifyNonemptyOilParking(dispatch, data);
                        break;
                    case 'activity':
                        // Ограничить размер очереди, удаляя самые старые сообщения при переполнении.
                        if (messageQueue.length >= MAX_QUEUE_SIZE) {
                            messageQueue.shift(); // Удалить самое старое сообщение.
                        }
                        messageQueue.push(setTransportItemsActivity(data));
                        break;
                    case 'cmd_send':
                        dispatch(addTransportItemsCommand(data));
                        break;
                }
            }
            catch (e) {
                console.error(e);
            }
        });
        // Функция для порционной обработки накопленной очереди при возврате на вкладку.
        const processQueueInBatches = (queue, dispatch) => {
            if (isProcessingHiddenQueue || queue.length === 0) {
                return;
            }
            isProcessingHiddenQueue = true;
            const processBatch = () => {
                if (queue.length === 0) {
                    isProcessingHiddenQueue = false;
                    return;
                }
                // Взять порцию сообщений для обработки.
                const messageBatch = queue.splice(0, BATCH_SIZE);
                // Обработать порцию в одном батче Redux.
                batch(() => {
                    for (const message of messageBatch) {
                        dispatch(message);
                    }
                });
                // Продолжить обработку следующей порции с небольшой задержкой.
                // Используем requestAnimationFrame для более плавной обработки (≈16ms при 60fps).
                if (queue.length > 0) {
                    requestAnimationFrame(() => {
                        setTimeout(processBatch, 0);
                    });
                }
                else {
                    isProcessingHiddenQueue = false;
                }
            };
            // Начать обработку первой порции с минимальной задержкой.
            requestAnimationFrame(() => {
                setTimeout(processBatch, 0);
            });
        };
        // Установим интервал такта цикла событий в один проход рендеринга.
        messageInterval = setInterval(() => {
            batch(() => {
                for (const message of messageQueue) {
                    dispatch(message);
                }
                messageQueue = [];
            });
        }, 5000);
        // Обработчик изменения видимости вкладки для предотвращения накопления сообщений.
        visibilityHandler = () => {
            if (document.visibilityState === 'hidden') {
                // Запомнить время когда вкладка стала неактивной.
                hiddenSince = Date.now();
                // Очистить обычную очередь при скрытии вкладки.
                // Критичные сообщения продолжают накапливаться в hiddenQueue.
                messageQueue = [];
            }
            else if (document.visibilityState === 'visible') {
                // Вычислить время неактивности.
                const inactiveTime = hiddenSince ? Date.now() - hiddenSince : 0;
                // Накопленная очередь при перезапросе отбрасывается, поэтому снимок ТС берется с сервера,
                // иначе в списке ТС останутся устаревшие значки (например, «Потеря связи»).
                const shouldReload = isListReloadPending ||
                    hiddenQueue.length >= RELOAD_THRESHOLD ||
                    inactiveTime >= Math.min(INACTIVE_TIME_THRESHOLD, config.mapTransportListOptions.reloadAfterHiddenMs);
                if (shouldReload) {
                    requestTransportListReload();
                    // Очистить очередь после перезапроса.
                    hiddenQueue = [];
                    isProcessingHiddenQueue = false;
                }
                else if (hiddenQueue.length > 0) {
                    // Если очередь небольшая - обработать накопленные критичные сообщения порциями.
                    processQueueInBatches(hiddenQueue, dispatch);
                }
                // Очистить обычную очередь, новые сообщения будут обрабатываться сразу.
                messageQueue = [];
                hiddenSince = null;
            }
        };
        document.addEventListener('visibilitychange', visibilityHandler);
    };
    // Обработчик события отключения WebSocket вручную.
    const closeEvent = (dispatch) => {
        // Закрыть сокетное соединение
        closeOpenConnection(4000);
        cancelTransportListReload();
        // Очистим интервал такта цикла событий.
        if (messageInterval) {
            clearInterval(messageInterval);
            messageQueue = [];
            hiddenQueue = [];
            isProcessingHiddenQueue = false;
            hiddenSince = null;
            messageInterval = null;
        }
        // Удалить обработчик видимости вкладки.
        if (visibilityHandler) {
            document.removeEventListener('visibilitychange', visibilityHandler);
            visibilityHandler = null;
        }
        // Отправка действий для уведомления Redux о закрытие соединения.
        dispatch(setWebsocketStatus('CLOSED'));
    };
    // Обработчик событий отправки сообщения WebSocket.
    const sendEvent = (dispatch, action) => {
        if (websocket) {
            websocket.send(JSON.stringify(action.payload));
        }
    };
    // Обработчик события закрытия WebSocket.
    const closeOpenConnection = (code, reason) => {
        if (websocket) {
            websocket.close(code || 1000, reason || 'WebSocket connection closed');
            websocket = null;
            hasOpened = false;
        }
    };
    // Обработчик события восстановления сломанного соединение WebSocket.
    const restoreBrokenConnection = (dispatch) => {
        websocket = null;
        // Отправка действий для уведомления Redux о ожидании соединения.
        dispatch(setWebsocketStatus('PENDING'));
        // Попытаться немедленно переподключиться, вызвав connect с утверждениями,
        // что аргументы соответствуют типам, которые мы ожидаем.
        connectEvent(dispatch);
        // Попытка повторного подключения через интервал.
        reconnectionInterval = setInterval(() => {
            // Закрыть соединение с причиной новой попытки.
            closeOpenConnection(4001);
            // Отправка действий для уведомления Redux о ожидании соединения.
            dispatch(setWebsocketStatus('PENDING'));
            // Вызвать соединение снова, таким же образом.
            connectEvent(dispatch);
        }, 12000);
    };
    // Проверка условий при повторном подключении, если соединение когда-либо было успешно открыто,
    // и в настоящее время мы не пытаемся переподключиться.
    // Это предотвращает повторяющиеся циклы переподключения к соединениям, которые не были
    // успешно открыты ранее, например ошибки net::ERR_CONNECTION_REFUSED.
    // Это также предотвращает запуск нескольких циклов попыток переподключения.
    const canAttemptReconnect = () => {
        return hasOpened && reconnectionInterval == null;
    };
    return (storeInstance) => (next) => (action) => {
        // Сохранить ссылку на store для использования в обработчиках.
        store = storeInstance;
        switch (action.type) {
            case 'SESSION_DELETE':
                parkingAlertTracker.reset();
                return next(action);
            case 'WEBSOCKET_OPEN_CONNECT':
                connectEvent(store.dispatch);
                break;
            case 'WEBSOCKET_CLOSE_CONNECT':
                closeEvent(store.dispatch);
                break;
            case 'WEBSOCKET_STOP_CONNECT':
                closeEvent(store.dispatch);
                break;
            case 'WEBSOCKET_SEND_MESSAGE':
                sendEvent(store.dispatch, action);
                break;
            default:
                return next(action);
        }
    };
};
export default websocketMiddleware;
